/**
 * The theatre the camera may not leave.
 *
 * Owner, 2026-09-15: *"det ska inte gå att zooma ut längre än hela sicilien och
 * man ska inte kunna panorera bort från sicilien."*
 * Then, having tried it: *"Just nu syns inte hela sicilien i max utzoom."*
 *
 * ── WHY THE FIRST ATTEMPT COULD NOT WORK ───────────────────────────────────
 * It used MapLibre's `maxBounds`, which requires the **viewport to fit inside**
 * the box. The zoom floor is the zoom where the viewport is **as large as** the
 * island. Screens are not shaped like Sicily, so at that zoom the viewport
 * overflows the box in one axis and MapLibre pulls the zoom back in — cropping
 * the north and south coasts, which is exactly what he saw.
 *
 * It is not a tuning problem. On a phone the viewport must show roughly four
 * degrees of latitude to fit the island's width; a box large enough to contain
 * that reaches past Malta, at which point the fence no longer fences anything.
 *
 * ── WHAT IS FENCED INSTEAD: THE CENTRE ─────────────────────────────────────
 * The camera **centre** is clamped to the island. Zooming out to the whole
 * island then always works, because nothing constrains the viewport; and
 * panning away is impossible, because the centre cannot leave. Sea is visible
 * around the coast, which for an amphibious campaign is the truer picture
 * anyway — the landings came from it.
 *
 * ⚠️ This is a *scenario* constraint, not an engine one. Kartkrig's theatres can
 * be founded anywhere on Earth; Husky is one fixed island. The fence lives here
 * so a different theatre only has to supply different bounds.
 */

export type Bounds = {
  west: number;
  south: number;
  east: number;
  north: number;
};

/**
 * Sicily, plus a margin for the Messina strait and the sea approaches.
 *
 * The same box the road graph was built from. If the camera could settle over
 * ground the graph does not cover, the player would be looking at roads no unit
 * can route along.
 */
export const SICILY_BOUNDS: Bounds = {
  west: 12.35,
  south: 36.6,
  east: 15.7,
  north: 38.35,
};

/**
 * Where the camera centre may go.
 *
 * The island itself: pan to the far corner and that corner is in the middle of
 * the screen, with sea around it. Anything wider would let the island slide off
 * the edge, which is the thing being prevented.
 */
export const CAMERA_BOUNDS: Bounds = SICILY_BOUNDS;

/**
 * How far in the camera may go.
 *
 * Owner, 2026-09-15, settling it after two false starts — one his, one mine:
 * *"rutter beräknas på z11. max utzoom är hela Sicilien och max inzoom är z12."*
 *
 * ⚠️ **The picture is therefore finer than the simulation**, and that is the
 * direction previously argued against here. The road graph is built at z11, so
 * at z12 the basemap draws minor roads no unit can march along. The trade is
 * deliberate: at z11 a desktop spanned ~44 km and formations stacked on one
 * town could not be told apart by eye, which is a cost paid on every order.
 * `map/stack.ts` spreads them, but spreading is not the same as seeing.
 *
 * ➡️ If a road turns out to be drawn but unroutable often enough to mislead,
 * the fix is to build the graph at z12 — not to quietly pull the ceiling back.
 * Zoom and graph level are one decision wearing two numbers.
 *
 * *(History, kept short: the ceiling was z11, then briefly z10 while the floor
 * was wrongly fixed at z10 on a misreading of his words. Both are gone.)*
 */
export const MAX_ZOOM = 12;

/** MapLibre's vector tiles are 512 px, which sets the world size at each zoom. */
const TILE_SIZE = 512;

export function boundsCentre(b: Bounds): [number, number] {
  return [(b.west + b.east) / 2, (b.south + b.north) / 2];
}

export function containsPoint(b: Bounds, lon: number, lat: number): boolean {
  return lon >= b.west && lon <= b.east && lat >= b.south && lat <= b.north;
}

/**
 * The nearest camera centre still inside the theatre.
 *
 * A fence, not a magnet: a centre already inside is returned untouched, so this
 * can be applied on every move without dragging the view around.
 */
export function clampCentre(b: Bounds, lon: number, lat: number): [number, number] {
  return [
    Math.min(Math.max(lon, b.west), b.east),
    Math.min(Math.max(lat, b.south), b.north),
  ];
}

/** Web Mercator northing as a fraction of the world square, 0 at the top. */
function worldY(lat: number): number {
  const rad = (lat * Math.PI) / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
}

/** The inverse, so a viewport's edges can be reported back as latitudes. */
function latFromWorldY(y: number): number {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
}

/**
 * The zoom at which the whole theatre just fits the viewport.
 *
 * Both axes are checked and the looser one wins, because the island has to fit
 * in full — a zoom that fits the width while cropping the north coast is not
 * "the whole of Sicily". Latitude is measured in Mercator space, not in raw
 * degrees, or the vertical fit would be wrong by the projection's stretch.
 */
export function minZoomForBounds(
  b: Bounds,
  widthPx: number,
  heightPx: number,
  padding = DEFAULT_PADDING,
): number {
  const widthFraction = ((b.east - b.west) / 360) * (1 + padding);
  const heightFraction = Math.abs(worldY(b.south) - worldY(b.north)) * (1 + padding);

  const zoomForWidth = Math.log2(widthPx / (TILE_SIZE * widthFraction));
  const zoomForHeight = Math.log2(heightPx / (TILE_SIZE * heightFraction));

  return Math.min(zoomForWidth, zoomForHeight);
}

/**
 * A sliver of sea around the island at the furthest zoom out.
 *
 * Two reasons, and the second is the one that bites. A map that ends exactly at
 * the coastline looks cramped, and for an amphibious campaign the approaches
 * are part of the picture. More importantly, an exact fit is a floating-point
 * knife-edge: the vertical extent goes through `log`, `atan` and `sinh`, so
 * "the island fits" and "the island is one ulp too tall" are the same
 * measurement. The margin moves the answer off the edge where it cannot be
 * decided.
 */
const DEFAULT_PADDING = 0.04;

/**
 * What a camera at this centre and zoom actually shows.
 *
 * Exists so "the whole island is visible" can be asserted as geometry rather
 * than eyeballed in a browser — the failure it guards against looked fine in
 * every unit test right up until the island was cropped on screen.
 */
export function viewportBounds(
  lon: number,
  lat: number,
  zoom: number,
  widthPx: number,
  heightPx: number,
): Bounds {
  const worldPx = TILE_SIZE * Math.pow(2, zoom);
  const halfWidthDeg = (360 * (widthPx / 2)) / worldPx;
  const halfHeightWorld = heightPx / 2 / worldPx;
  const centreY = worldY(lat);

  return {
    west: lon - halfWidthDeg,
    east: lon + halfWidthDeg,
    north: latFromWorldY(centreY - halfHeightWorld),
    south: latFromWorldY(centreY + halfHeightWorld),
  };
}
